import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Radio,
    Tooltip
} from "@material-ui/core";
import classNames from "classnames";
import React, { Component } from "react";
import styled from "styled-components";

import { player as playerApi, presentations as presentationsApi } from "apis/callables";
import { PdfCompressionTypes } from "common/constants";
import { ActionPermissionsObject, WorkspacePlanTier } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import getLogger, { LogGroup } from "js/core/logger";
import { fontManager } from "js/core/services/fonts";
import { download, trackActivity } from "js/core/utilities/utilities";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { exportToGoogleSlides } from "js/exporter/exportToGoogleSlides";
import UpgradeIcon from "js/Components/UpgradeIcon";
import Banner from "js/Components/Banner";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import CheckBox from "js/react/components/CheckBox";
import { BeautifulDialog, ShowDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { FlexBox } from "js/react/components/LayoutGrid";
import { themeColors } from "js/react/sharedStyles";

const logger = getLogger(LogGroup.EXPORT);

const DialogTitleShort = styled(DialogTitle)`
    && {
        padding-bottom: 20px;
    }
`;

const DialogContentNoPad = styled(DialogContent)`
    && {
        padding: 0;
    }
`;

const InfoText = styled.span`
    font-size: 12px;
    font-weight: normal;
    color: #676767;
`;

const ExportOptions = styled.div`
    padding: 0 25px;
    font-size: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;

    .export-option {
        margin: 0 10px;
    }

    .radio-button-label {
        font-weight: bold;
    }
`;

const CheckBoxContainer = styled.div`
    .MuiCheckbox-root {
        padding: 0 9px !important; 
    }
`;

const RadioShort = styled(Radio)`
    && {
        padding: 0 9px;
        pointer-events: ${props => props.$rowOnly ? "none" : "auto"};
    }
`;

const Description = styled.div`
    padding-left: 42px;
`;

const ExportOptionLabel = styled.div`
    font-weight: bold;
    opacity: ${props => props.$blocked ? 0.5 : 1};
`;

const ExportUpgradeBanner = styled(Banner)`
    &&& {
        background: #F7FAFC;
        border-color: ${themeColors.lightBlue};
        padding: 10px 15px;

        .bai-button {
            background: #FFFFFF;
            color: #333333;
        }

        .bai-button span {
            color: #333333;
        }
    }
`;

export default class ExportDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            exportType: WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use ? "editable" : "images",
            pdfCompressionType: "none",
            includeSkippedSlides: false,
            downloadFonts: true
        };
    }

    get isExportingEditable() {
        return this.state.exportType === "editable";
    }

    get canExportEditable() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE].use;
    }

    get canExportOffline() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.DESKTOP_APP].use;
    }

    get canUpgradeToUseExportEditable() {
        return WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.EXPORT_PRESENTATION_TO_EDITABLE, "use");
    }

    get canUpgradeToUseExportOffline() {
        return WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.DESKTOP_APP, "use");
    }

    get exportAllowed() {
        return WorkspaceController.actionPermissions[ActionPermissionsObject.EXPORT_PRESENTATION].use;
    }

    get shouldShowUpgradeBanner() {
        const { presentation } = this.props;
        const hasSmartSlides = presentation.hasSmartSlides() || !presentation.slides.length;

        return (
            hasSmartSlides &&
            !WorkspaceController.actionPermissions[ActionPermissionsObject.REMOVE_BAI_BRANDING].use &&
            WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.REMOVE_BAI_BRANDING, "use")
        );
    }

    handleChangeType = event => {
        this.setState({ exportType: event.target.value });
    }

    handleAccept = async () => {
        const { presentation, closeDialog } = this.props;
        let { includeSkippedSlides = false, exportType, pdfCompressionType, downloadFonts } = this.state;

        // force images if exporting an invalid combination
        if (!this.canExportEditable && exportType === "editable") {
            exportType = "images";
        } else if (!this.canExportOffline && exportType === "offline") {
            exportType = "images";
        }

        // verify there's anything to export
        if (!includeSkippedSlides) {
            const { models: slides } = presentation.slides;

            let skippedSlides;
            if (slides.some(slide => !slide.loaded)) {
                // some slide models aren't loaded so we have to pull their metadata from the api
                const slidesMetadata = await presentationsApi.getSlidesMetadata({ id: presentation.id });
                skippedSlides = slides.filter(slide => slidesMetadata[slide.id]?.isSkipped);
            } else {
                skippedSlides = slides.filter(slide => presentation.isSlideSkipped(slide));
            }

            const noSlidesExported = skippedSlides.length === slides.length;
            // nothing to export
            if (noSlidesExported) {
                ShowMessageDialog({
                    title: "No Slides to Export",
                    message: "This presentation appears to only contain skipped slides!"
                });
                return;
            }
        }

        // kick off the export
        if (exportType == "editable") {
            trackActivity("Presentation", "EditablePPTXExport", null, null, {}, { audit: true });

            // Download fonts if toggle is enabled and exporting editable PowerPoint
            if (downloadFonts) {
                await this.downloadFonts();
            }

            presentation.downloadExportCache("pptx", {
                destination: "local",
                includeSkippedSlides,
                pptxMode: "editable"
            });
        } else if (exportType == "images") {
            presentation.downloadExportCache("pptx", { destination: "local", includeSkippedSlides, pptxMode: "static" });
        } else if (exportType == "pdf") {
            presentation.downloadExportCache("pdf", { destination: "local", includeSkippedSlides, pdfCompressionType });
        } else if (exportType == "offline") {
            presentation.downloadExportCache("zip", { destination: "local", includeSkippedSlides });
        } else if (exportType == "google-slides") {
            exportToGoogleSlides({ includeSkippedSlides });
        }

        closeDialog();
    };

    downloadFonts = async () => {
        if (!this.canExportEditable) {
            return;
        }

        const progressDialog = ShowDialog(ProgressDialog, {
            title: "Exporting fonts..."
        });

        try {
            const canvasControllers = Object.values(PresentationEditorController.getCanvasControllers());

            // Making sure all canvases are rendered to allow fetching their fonts
            for (let canvasController of canvasControllers) {
                if (!canvasController.canvas) {
                    await canvasController.renderCanvas();
                }
            }

            let usedFonts = {};
            const getElementFonts = element => {
                Object.assign(usedFonts, element.fonts);
                Object.values(element.elements || {})
                    .forEach(getElementFonts);
            };
            canvasControllers.forEach(controller => Object.values(controller.canvas.layouter.canvasElement.elements).map(getElementFonts));

            usedFonts = Object.values(usedFonts);
            if (!usedFonts.some(font => font.isBuiltInFont)) {
                ShowMessageDialog({
                    title: "Can't download custom fonts",
                    message: "Your presentation uses custom uploaded fonts which cannot be downloaded from" +
                        " Beautiful.ai. Please install your original copies of these fonts before exporting your" +
                        " presentation."
                });
                return;
            } else if (usedFonts.some(font => font.isCustomFont)) {
                ShowMessageDialog({
                    title: "Can't download custom fonts",
                    message: "Your presentation uses both Beautiful.ai fonts and custom uploaded fonts. Custom uploaded" +
                        " fonts cannot be exported from Beautiful.ai. Please install your original copies of these fonts before exporting your" +
                        " presentation."
                });
            }

            for (const font of usedFonts.filter(font => font.isBuiltInFont)) {
                await fontManager.exportFontPackage(font.id);
            }
        } catch (err) {
            logger.error(err, "ExportDialog downloadFonts() failed to dowload used fonts");
        } finally {
            progressDialog.props.closeDialog();
        }
    }

    downloadOfflinePlayer = async () => {
        let os;
        if (window.navigator.platform.indexOf("Win") === 0) {
            os = "windows";
        }
        if (window.navigator.platform.indexOf("Mac") === 0) {
            os = "mac";
        }

        if (!os) {
            ShowMessageDialog({
                title: "Your OS is not supported",
                message: "Sorry, your operating system is not supported, offline player only supports MacOS and Windows"
            });
            return;
        }

        try {
            const { url } = await playerApi.getOfflinePlayerDownloadUrl({ os });
            download(url, `Beautiful.ai Offline Player${os === "windows" ? ".exe" : ".dmg"}`);
        } catch (err) {
            logger.error(err, "ExportDialog downloadOfflinePlayer() failed to dowload offline player", { os });
        }
    }

    handleToggleIncludeSkippedSlides = () => {
        this.setState({ includeSkippedSlides: !this.state.includeSkippedSlides });
    }

    handleImagesClick = () => {
        this.setState({ exportType: "images" });
    }

    handlePdfClick = () => {
        this.setState({ exportType: "pdf" });
    }

    handlePdfCompressionChange = event => {
        this.setState({ pdfCompressionType: event.target.value });
    }

    handlePdfCompressionToggle = () => {
        this.setState({
            pdfCompressionType: this.state.pdfCompressionType === "none" ? PdfCompressionTypes.HIGH : "none"
        });
    }

    showChangePlanDialog = cta => {
        ShowDialog(ChangePlanDialog, {
            featuredPlan: WorkspacePlanTier.PRO,
            analytics: { cta, ...this.props.presentation.getAnalytics() },
        });
    }

    handleEditableClick = () => {
        if (!this.canExportEditable) {
            if (this.canUpgradeToUseExportEditable) {
                this.showChangePlanDialog("ExportToPPTX");
            }
            this.setState({ exportType: "images" });
        } else {
            this.setState({ exportType: "editable" });
        }
    }

    handleGoogleSlidesClick = () => {
        if (!this.canExportEditable) {
            if (this.canUpgradeToUseExportEditable) {
                this.showChangePlanDialog("ExportToGoogleSlides");
            }
            this.setState({ exportType: "images" });
        } else {
            this.setState({ exportType: "google-slides" });
        }
    }

    handleOfflineClick = () => {
        if (!this.canExportOffline) {
            if (this.canUpgradeToUseExportOffline) {
                this.showChangePlanDialog("ExportOffline");
            }
            this.setState({ exportType: "images" });
        } else {
            this.setState({ exportType: "offline" });
        }
    }

    handleUpgradeBannerClick = () => {
        this.showChangePlanDialog("ExportWarningWatermark");
    }

    handleDownloadFontsToggle = value => {
        this.setState({ downloadFonts: value });
    }

    render() {
        const { presentation, closeDialog } = this.props;
        const { exportType, pdfCompressionType, includeSkippedSlides, downloadFonts } = this.state;

        if (!this.exportAllowed) {
            return (
                <BeautifulDialog
                    maxWidth="sm"
                    closeDialog={closeDialog}
                >
                    <DialogTitleShort>
                        <div>Export is not allowed for this workspace</div>
                    </DialogTitleShort>
                    <DialogActions>
                        <Button onClick={closeDialog}>Close</Button>
                    </DialogActions>
                </BeautifulDialog>
            );
        }

        return (
            <BeautifulDialog
                maxWidth="sm"
                closeDialog={closeDialog}
            >
                <DialogTitleShort>
                    <div>How would you like to export?</div>
                    <div>
                        <InfoText>
                            Some Beautiful.ai features cannot be exported, including slide animations and video.
                        </InfoText>
                    </div>
                    <CheckBoxContainer>
                        <CheckBox
                            value={includeSkippedSlides}
                            onChange={this.handleToggleIncludeSkippedSlides}
                            label={
                                <InfoText>Include skipped slides in export</InfoText>
                            }
                        />
                    </CheckBoxContainer>
                </DialogTitleShort>
                <DialogContentNoPad>
                    <ExportOptions>
                        <div className="export-option" onClick={this.handlePdfClick}>
                            <FlexBox verticalAlign="center" horizontalAlign="left">
                                <RadioShort
                                    checked={exportType === "pdf"}
                                    onChange={this.handleChangeType}
                                    $rowOnly
                                    value="pdf"
                                    name="exportType"
                                />
                                <ExportOptionLabel>PDF</ExportOptionLabel>
                            </FlexBox>

                            <Description>
                                <InfoText>Export slides as images.</InfoText>
                            </Description>

                            <Description>
                                <CheckBoxContainer style={{ marginTop: 10 }}>
                                    <CheckBox
                                        value={pdfCompressionType !== "none"}
                                        onChange={this.handlePdfCompressionToggle}
                                        label={
                                            <InfoText>Compress PDF - may result in lower image resolution.</InfoText>
                                        }
                                    />
                                </CheckBoxContainer>
                            </Description>
                        </div>

                        <div className="export-option" onClick={this.handleImagesClick}>
                            <FlexBox verticalAlign="center" horizontalAlign="left">
                                <RadioShort
                                    checked={exportType === "images"}
                                    onChange={this.handleChangeType}
                                    $rowOnly
                                    value="images"
                                    name="exportType"
                                />
                                <ExportOptionLabel>PowerPoint</ExportOptionLabel>
                            </FlexBox>

                            <Description>
                                <InfoText>Export slides as images.</InfoText>
                            </Description>
                        </div>

                        <div className="export-option" onClick={this.handleEditableClick}>
                            <FlexBox verticalAlign="center" horizontalAlign="left">
                                <RadioShort
                                    checked={exportType === "editable"}
                                    onChange={this.handleEditableClick}
                                    $rowOnly
                                    value="editable"
                                    name="exportType"
                                    disabled={!this.canExportEditable && !this.canUpgradeToUseExportEditable}
                                />
                                <ExportOptionLabel $blocked={!this.canExportEditable && this.canUpgradeToUseExportEditable}>Editable PowerPoint</ExportOptionLabel>
                                {this.canUpgradeToUseExportEditable && <UpgradeIcon style={{ marginLeft: 8 }} />}
                            </FlexBox>
                            { this.canExportEditable && (
                                <Description>
                                    <InfoText>
                                        <span>You may notice slight differences in exported slides. Fonts should be installed after download. </span>
                                    </InfoText>
                                    <div style={{ marginTop: 10 }}>
                                        <FlexBox verticalAlign="center" horizontalAlign="left" onClick={() => this.handleDownloadFontsToggle(true)}>
                                            <RadioShort
                                                checked={downloadFonts === true}
                                                onChange={() => this.handleDownloadFontsToggle(true)}
                                                value="true"
                                                name="downloadFonts"
                                            />
                                            <InfoText>Download fonts</InfoText>
                                        </FlexBox>
                                        <FlexBox verticalAlign="center" horizontalAlign="left" onClick={() => this.handleDownloadFontsToggle(false)}>
                                            <RadioShort
                                                checked={downloadFonts === false}
                                                onChange={() => this.handleDownloadFontsToggle(false)}
                                                value="false"
                                                name="downloadFonts"
                                            />
                                            <Tooltip title="PowerPoint will replace unsupported fonts if you don't download them with your export; this may change how your slides look.">
                                                <InfoText>Do not download fonts</InfoText>
                                            </Tooltip>
                                        </FlexBox>
                                    </div>
                                </Description>)
                            }
                        </div>

                        <div className="export-option" onClick={this.handleGoogleSlidesClick}>
                            <FlexBox verticalAlign="center" horizontalAlign="left">
                                <RadioShort
                                    checked={exportType === "google-slides"}
                                    onChange={this.handleGoogleSlidesClick}
                                    $rowOnly
                                    value="google-slides"
                                    name="exportType"
                                    disabled={!this.canExportEditable && !this.canUpgradeToUseExportEditable}
                                />
                                <ExportOptionLabel $blocked={!this.canExportEditable && this.canUpgradeToUseExportEditable}>Google Slides</ExportOptionLabel>
                                {this.canUpgradeToUseExportEditable && <UpgradeIcon style={{ marginLeft: 8 }} />}
                            </FlexBox>

                            {
                                this.canExportEditable &&
                                <Description>
                                    <InfoText>Slides are editable and will be saved to your Google Drive as a Google Slides presentation.</InfoText>
                                </Description>
                            }
                        </div>

                        <div className="export-option" onClick={this.handleOfflineClick}>
                            <FlexBox verticalAlign="center" horizontalAlign="left">
                                <RadioShort
                                    checked={exportType === "offline"}
                                    onChange={this.handleOfflineClick}
                                    $rowOnly
                                    value="offline"
                                    name="exportType"
                                    disabled={!this.canExportOffline && !this.canUpgradeToUseExportOffline}
                                />
                                <ExportOptionLabel $blocked={!this.canExportOffline && this.canUpgradeToUseExportOffline}>Present Offline</ExportOptionLabel>
                                {this.canUpgradeToUseExportOffline && <UpgradeIcon style={{ marginLeft: 8 }} />}
                            </FlexBox>

                            {
                                this.canExportOffline &&
                                <Description>
                                    <InfoText>
                                        <a
                                            className={classNames({
                                                ["link-blue"]: true,
                                                ["disabled"]: !this.canExportOffline,
                                            })}
                                            onClick={this.downloadOfflinePlayer}
                                        >
                                            Install the Beautiful.ai Offline Player
                                        </a>
                                    </InfoText>
                                </Description>
                            }
                        </div>
                        {this.shouldShowUpgradeBanner && (
                            <ExportUpgradeBanner
                                headerText="Export without Beautiful.ai branding"
                                bodyText="Upgrade to remove watermarks from all slides, and export editable files."
                                buttonLabel="Upgrade"
                                onButtonClick={this.handleUpgradeBannerClick}
                            />
                        )}
                    </ExportOptions>

                </DialogContentNoPad>
                <DialogActions>
                    <Button onClick={closeDialog}>Cancel</Button>
                    <Button onClick={this.handleAccept} color="primary">
                        Export
                    </Button>
                </DialogActions>
            </BeautifulDialog>
        );
    }
}
